import { PortableText, PortableTextBlock } from '@portabletext/react';
import clsx from 'clsx';
import { GatsbyImage } from 'gatsby-plugin-image';
import { twMerge } from 'tailwind-merge';

import { AccordionItems } from 'components/AccordionItems';
import { Container, Section } from 'components/GridV2';
import { ShortArrow } from 'components/icons';
import { UnorderedList } from 'components/rebrand/CheckList';
import CustomPortableText from 'components/rebrand/CustomPortableText';
import { GetKOHOBtn } from 'components/rebrand/GetKOHOBtn';

import { IQueryParams, ITrackEventProps } from 'typescriptDefs';
import { IRentersFeatures } from 'typescriptDefs/pages/renters';

const Features = ({
  features,
  featuresTracking,
}: {
  features: IRentersFeatures[];
  featuresTracking: {
    queryParams: IQueryParams;
    trackEventProps: ITrackEventProps;
  };
}) => {
  return (
    <>
      {features.map((feature, index) => (
        <IndividualFeature
          feature={feature}
          index={index}
          featuresTracking={featuresTracking}
          key={feature.buttonText}
        />
      ))}
    </>
  );
};

export default Features;

const IndividualFeature = ({
  feature,
  index,
  featuresTracking,
}: {
  feature: IRentersFeatures;
  index: number;
  featuresTracking: {
    queryParams: IQueryParams;
    trackEventProps: ITrackEventProps;
  };
}) => {
  const isEven = index % 2 === 0;

  return (
    <Section className={clsx(isEven ? 'bg-primary-50' : 'bg-white')}>
      <Container
        className={twMerge(
          clsx(
            'grid gap-16 px-32 py-64 md:gap-130 md:px-[unset] md:pb-120 md:pt-80',
            isEven ? 'md:grid-cols-[50%_auto]' : 'md:grid-cols-[auto_50%]',
          ),
        )}
      >
        <div className={twMerge(clsx('order-2 overflow-hidden rounded-16', !isEven ? 'md:order-2' : 'md:order-1'))}>
          <GatsbyImage alt={feature.image.altText} image={feature.image.img.asset.gatsbyImageData} />
        </div>
        <div className={clsx(isEven && 'order-1 md:order-2')}>
          <div
            className={twMerge(
              clsx('mb-16 [&_h2]:uppercase', isEven ? '[&_h2]:text-primary-300' : '[&_h2]:text-primary-400'),
            )}
          >
            <CustomPortableText content={feature.heading} />
          </div>
          <div className="mb-32 first:[&>p]:mb-32 [&_p]:text-md md:[&_p]:text-xl [&_ul]:mt-24">
            <CustomRentersPortableText content={feature.content} />
          </div>
          <GetKOHOBtn
            buttonLocation="renters"
            queryParams={featuresTracking.queryParams}
            trackEventProps={featuresTracking.trackEventProps}
            buttonClassName="mb-32"
          >
            {feature.buttonText}
          </GetKOHOBtn>
          <AccordionItems items={feature.accordionItems} />
        </div>
      </Container>
    </Section>
  );
};

const CustomRentersPortableText = ({ content }: { content: PortableTextBlock | PortableTextBlock[] }) => {
  return (
    <PortableText
      value={content}
      components={{
        marks: {
          nbsp: ({ children }) => <span className="whitespace-nowrap">{children}</span>,
        },
        block: {
          normal: ({ children }) => <p className="text-md font-normal leading-normal md:text-lg">{children}</p>,
        },
        list: {
          bullet: ({ children }) => <UnorderedList>{children}</UnorderedList>,
        },
        listItem: {
          bullet: ({ children }) => (
            <li className="flex list-none items-center gap-16">
              <ShortArrow className="[&_path]:text-grey-400" />
              <p>{children}</p>
            </li>
          ),
        },
      }}
    />
  );
};
